import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmailPreview = ({ selectedTemplate, personalizationData }) => {
  const [viewMode, setViewMode] = useState('desktop');
  const [showCode, setShowCode] = useState(false);

  const generateEmailHTML = () => {
    const { firstName, familySize, challenges, launchTiming } = personalizationData;
    
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="color-scheme" content="light dark">
    <meta name="supported-color-schemes" content="light dark">
    <title>Welcome to Cynizee - Your Family Organization Journey Begins</title>
    <style>
        /* Reset and base styles */
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #1A1A1A; background-color: #FFF8EF; }
        table { border-collapse: collapse; width: 100%; }
        img { max-width: 100%; height: auto; display: block; }
        
        /* Brand colors */
        .bg-navy { background-color: #0F2B3A; }
        .bg-teal { background-color: #1AA5A5; }
        .bg-coral { background-color: #FF5A3C; }
        .bg-gold { background-color: #D19A00; }
        .bg-cream { background-color: #FFF8EF; }
        .text-navy { color: #0F2B3A; }
        .text-teal { color: #1AA5A5; }
        .text-coral { color: #FF5A3C; }
        .text-white { color: #FFFFFF; }
        
        /* Layout */
        .container { max-width: 600px; margin: 0 auto; background-color: #FFFFFF; }
        .header { padding: 32px 24px; text-align: center; background: linear-gradient(135deg, #0F2B3A 0%, #1AA5A5 100%); }
        .content { padding: 40px 24px; }
        .footer { padding: 24px; background-color: #F8F4ED; text-align: center; }
        
        /* Typography */
        .heading-1 { font-family: 'Poppins', sans-serif; font-size: 28px; font-weight: 700; line-height: 1.2; margin-bottom: 16px; }
        .heading-2 { font-family: 'Poppins', sans-serif; font-size: 24px; font-weight: 600; line-height: 1.3; margin-bottom: 12px; }
        .heading-3 { font-family: 'Poppins', sans-serif; font-size: 20px; font-weight: 600; line-height: 1.4; margin-bottom: 8px; }
        .body-text { font-size: 16px; line-height: 1.6; margin-bottom: 16px; }
        .small-text { font-size: 14px; line-height: 1.5; color: #6B7280; }
        
        /* Components */
        .logo { display: inline-flex; align-items: center; gap: 12px; }
        .hexagon { width: 48px; height: 48px; background: #FFFFFF; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
        .hexagon-inner { width: 24px; height: 24px; border: 3px solid #1AA5A5; transform: rotate(45deg); border-radius: 4px; }
        
        .cta-button { display: inline-block; padding: 16px 32px; background-color: #FF5A3C; color: #FFFFFF; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px; margin: 16px 0; }
        .cta-button:hover { background-color: #E53E3E; }
        
        .feature-card { background-color: #F8F4ED; border-radius: 12px; padding: 24px; margin: 16px 0; border-left: 4px solid #1AA5A5; }
        .social-links { display: flex; justify-content: center; gap: 16px; margin: 24px 0; }
        .social-link { display: inline-block; width: 40px; height: 40px; background-color: #1AA5A5; border-radius: 50%; text-align: center; line-height: 40px; color: #FFFFFF; text-decoration: none; }
        
        /* Responsive */
        @media only screen and (max-width: 600px) {
            .container { width: 100% !important; }
            .content { padding: 24px 16px !important; }
            .heading-1 { font-size: 24px !important; }
            .heading-2 { font-size: 20px !important; }
            .cta-button { padding: 14px 24px !important; font-size: 14px !important; }
        }
        
        /* Dark mode */
        @media (prefers-color-scheme: dark) {
            .container { background-color: #1A1A1A !important; }
            .content { background-color: #1A1A1A !important; color: #FFFFFF !important; }
            .body-text { color: #E5E5E5 !important; }
            .feature-card { background-color: #2A2A2A !important; }
        }
    </style>
</head>
<body>
    <div class="container">
        <!-- Header -->
        <div class="header">
            <div class="logo">
                <div class="hexagon">
                    <div class="hexagon-inner"></div>
                </div>
                <div>
                    <div style="font-family: 'Poppins', sans-serif; font-size: 24px; font-weight: 700; color: #FFFFFF;">Cynizee</div>
                    <div style="font-size: 12px; color: #E5E5E5; margin-top: -4px;">Marketing Hub</div>
                </div>
            </div>
        </div>
        
        <!-- Main Content -->
        <div class="content">
            <h1 class="heading-1 text-navy">Welcome to Your Family's New Chapter, ${firstName || 'Friend'}! 🏠</h1>
            
            <p class="body-text">
                Thank you for joining the Cynizee waitlist! You've just taken the first step toward transforming your family's daily chaos into organized harmony.
            </p>
            
            ${familySize ? `<p class="body-text">
                We see you have a family of <strong>${familySize}</strong> - we know how challenging it can be to keep everyone organized and on track. That's exactly why we built Cynizee.
            </p>` : ''}
            
            <div class="feature-card">
                <h3 class="heading-3 text-teal">What's Coming Your Way</h3>
                <p class="body-text">
                    🎯 <strong>Lock Mode:</strong> Transform screen time battles into productive family moments\n
                    📱 <strong>Smart Routines:</strong> Personalized task flows that actually work for your family\n
                    🎉 <strong>Celebration System:</strong> Turn completed tasks into family victories\n
                    🔒 <strong>Privacy-First:</strong> Your family data stays with your family, always
                </p>
            </div>
            
            ${challenges ? `<div class="feature-card">
                <h3 class="heading-3 text-coral">We Hear You on "${challenges}"</h3>
                <p class="body-text">
                    This is one of the most common challenges families face, and it's exactly what Cynizee was designed to solve. Our beta families have seen remarkable improvements in this area within just the first week.
                </p>
            </div>` : ''}
            
            <div style="text-align: center; margin: 32px 0;">
                <a href="https://cynizee.com/homepage" class="cta-button">
                    Explore Our Vision →
                </a>
            </div>
            
            <h2 class="heading-2 text-navy">What Happens Next?</h2>
            
            <div style="background-color: #FFF8EF; border-radius: 12px; padding: 24px; margin: 24px 0; border: 2px solid #D19A00;">
                <p class="body-text">
                    <strong>📅 ${launchTiming || 'Early 2024'}:</strong> We'll send you exclusive early access\n
                    <strong>💌 Weekly Updates:</strong> Behind-the-scenes development insights\n
                    <strong>🎁 Beta Perks:</strong> Special pricing and features for waitlist members\n
                    <strong>👨‍👩‍👧‍👦 Community Access:</strong> Connect with other organizing families
                </p>
            </div>
            
            <div style="background-color: #F0F9FF; border-radius: 12px; padding: 24px; margin: 24px 0; border-left: 4px solid #1AA5A5;">
                <h3 class="heading-3 text-teal">From Our Founder</h3>
                <p class="body-text" style="font-style: italic;">
                    "As a parent of three, I know the daily struggle of keeping everyone organized without constant battles. Cynizee isn't just another app - it's the solution I wished existed when my kids were younger. We're building something that actually works for real families."
                </p>
                <p class="small-text" style="margin-top: 12px;">
                    - Sarah Chen, Founder & CEO
                </p>
            </div>
            
            <div style="text-align: center; margin: 32px 0;">
                <h3 class="heading-3 text-navy">Stay Connected</h3>
                <div class="social-links">
                    <a href="#" class="social-link" title="Facebook">f</a>
                    <a href="#" class="social-link" title="Twitter">t</a>
                    <a href="#" class="social-link" title="Instagram">i</a>
                    <a href="#" class="social-link" title="LinkedIn">in</a>
                </div>
                <p class="small-text">
                    Follow us for daily family organization tips and development updates
                </p>
            </div>
            
            <div style="background-color: #FEF2F2; border-radius: 8px; padding: 16px; margin: 24px 0; border-left: 4px solid #FF5A3C;">
                <p class="small-text">
                    <strong>Referral Bonus:</strong> Know another family that could use Cynizee? 
                    <a href="#" style="color: #FF5A3C; text-decoration: none;">Share your referral link</a> 
                    and you'll both get exclusive early access perks!
                </p>
            </div>
        </div>
        
        <!-- Footer -->
        <div class="footer">
            <p class="small-text">
                <strong>Cynizee Marketing Hub</strong><br>
                Transforming family chaos into organized harmony<br>
                San Francisco, CA | support@cynizee.com
            </p>
            
            <div style="margin: 16px 0; padding: 16px 0; border-top: 1px solid #E5E5E5;">
                <p class="small-text">
                    <a href="https://cynizee.com/privacy-policy" style="color: #1AA5A5; text-decoration: none;">Privacy Policy</a> | 
                    <a href="https://cynizee.com/terms-of-service" style="color: #1AA5A5; text-decoration: none;">Terms of Service</a> | 
                    <a href="https://cynizee.com/contact" style="color: #1AA5A5; text-decoration: none;">Contact Us</a>
                </p>
            </div>
            
            <p class="small-text">
                You're receiving this email because you joined our waitlist at cynizee.com<br>
                <a href="#" style="color: #6B7280; text-decoration: none;">Update preferences</a> | 
                <a href="#" style="color: #6B7280; text-decoration: none;">Unsubscribe</a>
            </p>
            
            <p class="small-text" style="margin-top: 16px;">
                © ${new Date()?.getFullYear()} Cynizee Marketing Hub. All rights reserved.
            </p>
        </div>
    </div>
</body>
</html>`;
  };

  const copyToClipboard = () => {
    navigator.clipboard?.writeText(generateEmailHTML());
  };

  return (
    <div className="bg-white rounded-xl shadow-soft border border-border overflow-hidden">
      {/* Preview Controls */}
      <div className="flex items-center justify-between p-4 bg-surface border-b border-border">
        <div className="flex items-center space-x-4">
          <h3 className="font-heading font-semibold text-foreground">Email Preview</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('desktop')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'desktop' ?'bg-primary text-primary-foreground' :'text-text-secondary hover:text-foreground'
              }`}
            >
              <Icon name="Monitor" size={16} className="inline mr-1" />
              Desktop
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'mobile' ?'bg-primary text-primary-foreground' :'text-text-secondary hover:text-foreground'
              }`}
            >
              <Icon name="Smartphone" size={16} className="inline mr-1" />
              Mobile
            </button>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCode(!showCode)}
            iconName={showCode ? "Eye" : "Code"}
            iconPosition="left"
            iconSize={16}
          >
            {showCode ? 'Preview' : 'HTML Code'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={copyToClipboard}
            iconName="Copy"
            iconPosition="left"
            iconSize={16}
          >
            Copy HTML
          </Button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="p-4">
        {showCode ? (
          <div className="bg-gray-900 rounded-lg p-4 overflow-auto max-h-96">
            <pre className="text-green-400 text-sm">
              <code>{generateEmailHTML()}</code>
            </pre>
          </div>
        ) : (
          <div className={`mx-auto transition-all duration-300 ${
            viewMode === 'mobile' ? 'max-w-sm' : 'max-w-2xl'
          }`}>
            <div 
              className="border border-border rounded-lg overflow-hidden shadow-soft"
              style={{ 
                transform: viewMode === 'mobile' ? 'scale(0.8)' : 'scale(1)',
                transformOrigin: 'top center'
              }}
            >
              <iframe
                srcDoc={generateEmailHTML()}
                className="w-full h-96 border-0"
                title="Email Preview"
              />
            </div>
          </div>
        )}
      </div>

      {/* Template Info */}
      <div className="p-4 bg-surface border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-foreground">Template:</span>
            <span className="ml-2 text-text-secondary">{selectedTemplate}</span>
          </div>
          <div>
            <span className="font-medium text-foreground">Responsive:</span>
            <span className="ml-2 text-success">✓ Mobile & Desktop</span>
          </div>
          <div>
            <span className="font-medium text-foreground">Dark Mode:</span>
            <span className="ml-2 text-success">✓ Supported</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreview;